import React, {ReactNode} from 'react';

import {ErrorBoundary as SentryErrorBoundary} from '@sentry/react';
import type {Scope} from '@sentry/react';

import ErrorFallbackScreen from './components/ErrorFallbackScreen';

type ErrorBoundaryProps = {
  /**
   * Isolation point name, sent as the `error_boundary` tag (e.g. "root",
   * "route"). Lets Sentry distinguish a contained route crash from a
   * whole-app one.
   */
  boundary: string;
  children?: ReactNode;
  fallback?: ReactNode;
  /**
   * Overrides `mechanism.handled`. `Sentry.ErrorBoundary` derives it from the
   * presence of a fallback, which would report a crash that took the whole app
   * down as handled and hide it from `error.handled:false`, crash-rate and
   * user-misery.
   */
  handled?: boolean;
};

/**
 * Sentry-integrated error boundary. Unlike the previous hand-rolled
 * one, `Sentry.ErrorBoundary` attaches `componentStack`, links non-Error
 * throws via `error.cause` (no `new Error(JSON.stringify(err))` grouping
 * splinter), and reports with `handled: true` mechanism since a fallback is
 * shown.
 */
const ErrorBoundary = ({boundary, children, fallback, handled}: ErrorBoundaryProps) => {
  const tagBoundaryScope = (scope: Scope) => {
    scope.setTag('error_boundary', boundary);
  };
  return (
    <SentryErrorBoundary
      beforeCapture={tagBoundaryScope}
      fallback={<>{fallback ?? <ErrorFallbackScreen />}</>}
      handled={handled}
    >
      {children}
    </SentryErrorBoundary>
  );
};

export default ErrorBoundary;
